import React, { useEffect, useRef, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-toastify";

import { SpareItem } from "../../../component/spares/SpareItem";
import { LoadingSpinner } from "../../../component/spinners/LoadingSpinner";
import classes from "./productListPage.module.css";

import {
  selectProductListState,
  setProductListFilter,
  setProductListCursor,
} from "../../../store/productListFilterSlice";

import {
  selectAllProducts,
  selectProductListPagination,
  selectProductListStatus,
  useGetProductListQuery,
  useUpdateProductMutation,
} from "../../../services/productListApiSice";

export const ProductListPage = () => {
  const { category, inventory_id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const filterState = useSelector(selectProductListState);
  const products = useSelector(selectAllProducts);
  const pagination = useSelector(selectProductListPagination);
  const status = useSelector(selectProductListStatus);

  const initializedRef = useRef(false);
  const lastRouteRef = useRef({ category: null, inventory_id: null });

  // Initialize Redux Filters on route change
  useEffect(() => {
    const routeChanged =
      lastRouteRef.current.category !== category ||
      lastRouteRef.current.inventory_id !== inventory_id;

    if (routeChanged) {
      lastRouteRef.current = { category, inventory_id };
      initializedRef.current = false;
    }

    if (!initializedRef.current && category && inventory_id) {
      dispatch(
        setProductListFilter({
          category,
          inventory_id,
          cursor: 0,
        })
      );
      initializedRef.current = true;
    }
  }, [category, inventory_id, dispatch]);

  // RTK Query
  const shouldSkipQuery = !filterState.category || !filterState.inventory_id;
  const { refetch, isFetching, isLoading } = useGetProductListQuery(
    {
      category: filterState.category,
      inventory_id: filterState.inventory_id,
      cursor: filterState.cursor,
      pagination: filterState.pagination,
    },
    {
      skip: shouldSkipQuery,
      refetchOnMountOrArgChange: true,
    }
  );

  // Mutation
  const [updateProduct, { isLoading: isUpdating }] = useUpdateProductMutation();

  // Infinite Scroll
  const loaderRef = useRef(null);

  const handleIntersection = useCallback(() => {
    if (pagination.hasMore && !isFetching) {
      dispatch(setProductListCursor(pagination.nextCursor));
    }
  }, [pagination.hasMore, pagination.nextCursor, isFetching, dispatch]);

  useEffect(() => {
    if (!loaderRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting) handleIntersection();
      },
      { root: null, rootMargin: "200px", threshold: 0.1 }
    );

    observer.observe(loaderRef.current);

    return () => observer.disconnect();
  }, [handleIntersection]);

  // Navigation
  const handleProductDetail = useCallback(
    (productId) => productId && navigate(`${productId}`),
    [navigate]
  );

  // Hot Sell Toggle
  const handleHotSellClick = useCallback(
    async (itemId) => {
      if (!itemId) return toast.error("Invalid product");

      try {
        const response = await updateProduct({
          category,
          id: itemId,
        }).unwrap();

        toast.success(
          response?.message?.displayMessage ||
            response?.message ||
            "Product updated successfully!"
        );
      } catch (err) {
        toast.error(
          err?.data?.message?.displayMessage ||
            err?.data?.message ||
            err?.message ||
            "Failed to update product."
        );
      }
    },
    [updateProduct, category]
  );

  // Retry
  const handleRetry = useCallback(() => {
    dispatch(setProductListCursor(0));
    refetch();
  }, [dispatch, refetch]);

  // Render Loading/Error/Empty States
  if (status.isLoading && products.length === 0) return <LoadingState />;
  if (status.isError && products.length === 0)
    return <ErrorState error={status.error} onRetry={handleRetry} />;
  if (!status.isLoading && products.length === 0)
    return <EmptyState inventoryId={inventory_id} />;

  return (
    <div className={classes.box}>
      <Header inventoryId={inventory_id} pagination={pagination} />

      <div className={classes.box__list}>
        {products.map((product) => (
          <SpareItem
            key={product.id}
            item={product}
            onClick={handleProductDetail}
            onHotSellClick={handleHotSellClick}
            isUpdating={isUpdating}
          />
        ))}
      </div>

      <div
        ref={loaderRef}
        className={classes.box__wrapper}
        aria-live="polite"
        aria-busy={isFetching}
      >
        {isFetching && <LoadingSpinner />}
      </div>

      {!pagination.hasMore && products.length > 0 && (
        <div className={classes.box__endMessage}>
          <p>All products loaded</p>
        </div>
      )}

      {status.isError && products.length > 0 && (
        <ErrorToast onRetry={handleRetry} />
      )}
    </div>
  );
};

/** Helper Components */
const Header = ({ inventoryId, pagination }) => (
  <div className={classes.box__head}>
    <h1 className={classes.box__head__title}>Inventory List: {inventoryId}</h1>
    {/* <div className={classes.box__head__meta}>
      <span className={classes.box__head__count}>
        Total Items: {pagination.currentCount}
      </span>
    </div> */}
  </div>
);

const LoadingState = () => (
  <div className={classes.box__wrapper}>
    <LoadingSpinner />
    <p className={classes.loadingState__text}>Loading products...</p>
  </div>
);

const ErrorState = ({ error, onRetry }) => (
  <div className={classes.errorState}>
    <div className={classes.errorState__icon}>⚠️</div>
    <h2 className={classes.errorState__title}>Unable to Load Products</h2>
    <p className={classes.errorState__message}>
      {error?.data?.message || error?.message || "An error occurred."}
    </p>
    <button onClick={onRetry} className={classes.errorState__button}>
      Try Again
    </button>
  </div>
);

const EmptyState = ({ inventoryId }) => (
  <div className={classes.emptyState}>
    <div className={classes.emptyState__icon}>📦</div>
    <h2 className={classes.emptyState__title}>No Products Found</h2>
    <p className={classes.emptyState__message}>
      The inventory "{inventoryId}" doesn't contain any products yet.
    </p>
  </div>
);

const ErrorToast = ({ onRetry }) => (
  <div className={classes.box__errorToast}>
    <div className={classes.errorToast}>
      <span className={classes.errorToast__icon}>⚠️</span>
      <span className={classes.errorToast__message}>
        Failed to load more products
      </span>
      <button
        onClick={onRetry}
        className={classes.errorToast__button}
        type="button"
      >
        Retry
      </button>
    </div>
  </div>
);

export default ProductListPage;
